import { Line } from '@react-three/drei';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import * as THREE from 'three';

import { MeasureKind, UnitsByMeasure } from 'convert';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { DEFAULT_INDICATOR_COLOR, DEFAULT_INDICATOR_FONT_SIZE_XS, DEFAULT_INDICATOR_LINE_WIDTH, MM_TO_INTERNAL_FACTOR } from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RoundedBoxWithText from '../RoundedBoxWithText';

interface ProductDimensionsIndicatorProps {
  length: number; // Product length in mm
  depth: number; // Product depth in mm
  rotation: number; // Rotation angle in radians
}

const RoomComponentDimensionsIndicator: React.FC<ProductDimensionsIndicatorProps> = ({ length, depth, rotation }) => {
  const { unitType } = useUnitContext();
  const { locale } = useLanguage();
  const intl = useIntl();
  const OFFSET = 0.7; // Distance of indicator lines from product
  const EXTENSION = 0.2; // Length of the perpendicular extension lines

  // Get localized unit labels
  const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "meter"), [locale]);
  const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "foot"), [locale]);

  // Convert dimensions based on unit type (meters or feet)
  const localizedlength = useMemo(() => {
    const value = unitType === EUnitType.METRIC 
      ? (length / 1000) 
      : convertTo(length / 1000, "meters" as UnitsByMeasure<MeasureKind.Length>, "feet" as UnitsByMeasure<MeasureKind.Length>);

    return `${localizeNumber(value.toFixed(2), locale)} ${unitType === EUnitType.METRIC ? mLocalizedLabel : ftLocalizedLabel}`;
  }, [length, unitType, locale]);

  const localizeddepth = useMemo(() => {
    const value = unitType === EUnitType.METRIC 
      ? (depth / 1000) 
      : convertTo(depth / 1000, "meters" as UnitsByMeasure<MeasureKind.Length>, "feet" as UnitsByMeasure<MeasureKind.Length>);

    return `${localizeNumber(value.toFixed(2), locale)} ${unitType === EUnitType.METRIC ? mLocalizedLabel : ftLocalizedLabel}`;
  }, [depth, unitType, locale]);

  // Convert dimensions from mm to internal units
  const halflength = (length / 2) * MM_TO_INTERNAL_FACTOR;
  const halfdepth = (depth / 2) * MM_TO_INTERNAL_FACTOR;

  // Move the length indicator **below** the product instead of above
  const lengthStart = new THREE.Vector3(-halflength, -halfdepth - OFFSET, 0);
  const lengthEnd = new THREE.Vector3(halflength, -halfdepth - OFFSET, 0);
  const lengthTextPosition = new THREE.Vector3(0, -halfdepth - OFFSET * 2, 0);

  // Perpendicular lines for length
  const lengthStartPerpendicularStart = lengthStart.clone();
  const lengthStartPerpendicularEnd = lengthStart.clone().add(new THREE.Vector3(0, EXTENSION, 0)); // Flipped to point upwards
  const lengthEndPerpendicularStart = lengthEnd.clone();
  const lengthEndPerpendicularEnd = lengthEnd.clone().add(new THREE.Vector3(0, EXTENSION, 0)); // Flipped to point upwards

  // Define points for depth (vertical line) - remains unchanged
  const depthStart = new THREE.Vector3(halflength + OFFSET, -halfdepth, 0);
  const depthEnd = new THREE.Vector3(halflength + OFFSET, halfdepth, 0);
  const depthTextPosition = new THREE.Vector3(halflength + OFFSET * 2, 0, 0);

  // Perpendicular lines for depth
  const depthStartPerpendicularStart = depthStart.clone();
  const depthStartPerpendicularEnd = depthStart.clone().sub(new THREE.Vector3(EXTENSION, 0, 0));
  const depthEndPerpendicularStart = depthEnd.clone();
  const depthEndPerpendicularEnd = depthEnd.clone().sub(new THREE.Vector3(EXTENSION, 0, 0));

  return (
    <group>
      {/* Length Indicator (Now Below) */}
      <Line points={[lengthStart, lengthEnd]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <Line points={[lengthStartPerpendicularStart, lengthStartPerpendicularEnd]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <Line points={[lengthEndPerpendicularStart, lengthEndPerpendicularEnd]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <group position={lengthTextPosition} rotation={[0, 0, rotation]}>
        <RoundedBoxWithText
          position={new THREE.Vector3(0, 0, 0)}
          fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS}
          textColor="black"
          text={localizedlength}
        />
      </group>

      {/* Depth Indicator (Unchanged) */}
      <Line points={[depthStart, depthEnd]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <Line points={[depthStartPerpendicularStart, depthStartPerpendicularEnd]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <Line points={[depthEndPerpendicularStart, depthEndPerpendicularEnd]} color={DEFAULT_INDICATOR_COLOR} lineWidth={DEFAULT_INDICATOR_LINE_WIDTH} />
      <group position={depthTextPosition} rotation={[0, 0, rotation]}>
        <RoundedBoxWithText
          position={new THREE.Vector3(0, 0, 0)}
          fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS}
          textColor="black"
          text={localizeddepth}
        />
      </group>
    </group>
  );
};

export default RoomComponentDimensionsIndicator;
